import { useQuery } from '@tanstack/react-query';
import { get } from '@truefit/http-utils';
import { isUUID } from 'validator';
import { AxiosError } from 'axios';

import toGameModel from '../services/toGameModel';
import { GameModel } from '../types';
import useCurrentGameId from '@/shared/hooks/useCurrentGameId';
import { defaultGcTime, defaultStaleTime } from '@/shared/constants';

export const queryKey = (gameId: string) => ['Games', gameId] as const;

const DEFAULT_GAME_MODEL: GameModel = {
  name: '',
  externalid: 0,
  startTimestamp: null,
  endTimestamp: null,
  awayTeam: {} as any,
  homeTeam: {} as any,
  status: '',
  awayTeamScore: 0,
  homeTeamScore: 0,
  uniqueId: '',
  homeTeamColor: '',
  awayTeamColor: '',
  available: false,
  enableTracking: false,
  trackingOffset: '0',
  version: null,
  season: { league: { name: '' } },
  venue: { name: '', id: '', timezone: '' }
};

const useCurrentGameQuery = () => {
  const gameId = useCurrentGameId();
  
  return useQuery({
    queryKey: queryKey(gameId),
    queryFn: async () => {
      if (!gameId || !isUUID(gameId)) {
        throw new Error('Invalid game ID');
      }
      
      const { data } = await get<GameModel>(`/v1.0/games/${gameId}`);
      return toGameModel(data);
    },
    retry: (failureCount, error) => {
      // Only retry for network errors, not for 404s or other client errors
      if (error instanceof AxiosError && error.response?.status && error.response.status >= 500) {
        return failureCount < 3;
      }
      return false;
    },
    staleTime: defaultStaleTime, // Data considered fresh for 30 seconds
    gcTime: defaultGcTime,  // Data will be garbage collected after 5 minutes
    refetchOnWindowFocus: Boolean(gameId) && isUUID(gameId), // refetch ONLY if enabled and gameId is valid
    // Don't fetch if gameId is invalid
    enabled: Boolean(gameId) && isUUID(gameId),
    select: (data) => {
      // Transform or validate data if needed
      return data ?? DEFAULT_GAME_MODEL;
    }
  });
};

export default useCurrentGameQuery;